import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Users, Sparkles, Zap, Shield, BarChart3 } from 'lucide-react';
import { MachhaCharacter } from '../lib/machhaAsset';
import { Button } from '../components/UI/Button';
import { track, EVENTS } from '../lib/mixpanel';
import { usePageTitle } from '../hooks/usePageTitle';
import { clayStaggerContainer, clayStaggerItem } from '../constants/animations';
import styles from './BecomeACreatorPage.module.css';

const FEATURES = [
  {
    icon: Camera,
    title: 'Professional FTP Ingestion',
    desc: 'Connect cameras directly via FTP — photos appear instantly as they shoot.',
  },
  {
    icon: Sparkles,
    title: 'AI Face Recognition',
    desc: 'Guests find their photos automatically. No tagging, no searching.',
  },
  {
    icon: Users,
    title: 'Shared Albums',
    desc: 'Create beautiful guest-facing albums with one QR code join link.',
  },
  {
    icon: Zap,
    title: 'Live Slideshows',
    desc: 'Cast real-time photo slideshows to any screen at your venue.',
  },
  {
    icon: Shield,
    title: 'Privacy Controls',
    desc: 'Full control over who sees what — per-album guest visibility settings.',
  },
  {
    icon: BarChart3,
    title: 'Export & Editing',
    desc: 'Bulk-export with custom filters, overlays, and platform presets.',
  },
];

const HOW_IT_WORKS = [
  {
    step: '01',
    title: 'Apply',
    desc: 'Tell us about your events — takes two minutes.',
  },
  {
    step: '02',
    title: 'Get approved',
    desc: "We review and activate your account, usually within a day.",
  },
  {
    step: '03',
    title: 'Start creating',
    desc: 'Create your first event, add an album, share the link with guests.',
  },
];

export function BecomeACreatorPage() {
  usePageTitle('Become a Creator');
  const navigate = useNavigate();

  useEffect(() => {
    track(EVENTS.BECOME_CREATOR_PAGE_VIEWED);
  }, []);

  const handleCTA = () => {
    track(EVENTS.BECOME_CREATOR_CTA_CLICKED);
    navigate('/waitlist/onboarding');
  };

  return (
    <div className={styles.page}>
      {/* ── Hero ── */}
      <section className={styles.hero}>
        <motion.div
          className={styles['hero-inner']}
          variants={clayStaggerContainer}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={clayStaggerItem}>
            <MachhaCharacter state="Welcome" size={140} />
          </motion.div>
          <motion.h1 className={styles['hero-title']} variants={clayStaggerItem}>
            Create unforgettable<br />photo experiences
          </motion.h1>
          <motion.p className={styles['hero-subtitle']} variants={clayStaggerItem}>
            Pixel Machha gives photographers and event teams a professional-grade
            platform — FTP uploads, face recognition, live slideshows, and guest
            albums that just work.
          </motion.p>
          <motion.div variants={clayStaggerItem}>
            <Button variant="primary" size="lg" onClick={handleCTA}>
              Apply for creator access
            </Button>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Features grid ── */}
      <section className={styles.section}>
        <h2 className={styles['section-title']}>Everything you need</h2>
        <motion.div
          className={styles['features-grid']}
          variants={clayStaggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {FEATURES.map(({ icon: Icon, title, desc }) => (
            <motion.div key={title} className={styles['feature-card']} variants={clayStaggerItem}>
              <div className={styles['feature-icon-wrap']}>
                <Icon size={22} />
              </div>
              <h3 className={styles['feature-title']}>{title}</h3>
              <p className={styles['feature-desc']}>{desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* ── How it works ── */}
      <section className={styles.section}>
        <h2 className={styles['section-title']}>How it works</h2>
        <div className={styles['steps-row']}>
          {HOW_IT_WORKS.map(({ step, title, desc }) => (
            <div key={step} className={styles['step-card']}>
              <span className={styles['step-number']}>{step}</span>
              <h3 className={styles['step-title']}>{title}</h3>
              <p className={styles['step-desc']}>{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className={styles['cta-section']}>
        <h2 className={styles['cta-title']}>Ready to get started?</h2>
        <p className={styles['cta-subtitle']}>
          Join a growing community of event photographers and organizers.
        </p>
        <Button variant="primary" size="lg" onClick={handleCTA}>
          Apply for creator access
        </Button>
      </section>
    </div>
  );
}
